import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Button } from '@mui/material';
import DrawerCDS from '@sharedCR/coreDesignSystem/Drawer/DrawerCDS';
import { normalizeBorrowerApplicationData } from 'app/pages/loanApplication/tabs/borrowers/EditEntityModal/Content/Content.utils';
import { buildPostClosingApplicationData } from 'app/pages/loanApplication/tabs/postClosing/EditDrawer/EditDrawer.utils';
import { usePostClosingSelectOptions } from 'app/pages/loanApplication/tabs/postClosing/hooks/usePostClosingSelectOptions';
import {
  normalizeContactsApplicationData,
  normalizeLoanAndLockApplicationData,
  normalizePropertyAndLoanApplicationData,
} from 'app/pages/loanApplication/tabs/propertyAndLoan/EditModal/Content/Content.utils';
import { buildAppraisalProductOptions } from 'app/pages/loanApplication/tabs/propertyAndLoan/EditModal/Content/MainContent/Appraisal/Appraisal.utils';
import { buildMiCompanyTypeChangeHandler } from 'app/pages/loanApplication/tabs/propertyAndLoan/EditModal/Content/MainContent/MortgageInsurance/MortgageInsurance.utils';
import { CUSTOM_FIELDS_TARGET } from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.constants';
import useCustomFieldsValidation from 'app/shared/components/customFields/CustomFieldsSection/hooks/useCustomFieldsValidation';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FeatureFlag } from 'constants/featureFlags';
import { PAGE_CODES } from 'constants/pageCodes';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';
import { checkIsLoanPurposeTypeBpl } from 'utils/loanApplication';
import { getConfigInfo } from 'utils/loanManagement';
import { logger } from 'utils/logger';

import { getInvestorsAction, getServicingCompaniesAction } from 'store/accounting/actions';
import {
  selectInvestorsOptions,
  selectServicingCompaniesOptions,
} from 'store/accounting/selectors';
import { getMasterPermissionsToPage, selectIsBrokerOrSubCompanyUser } from 'store/auth/selectors';
import { getLeadSourcesAction } from 'store/companies/actions';
import { getContactTypesAction } from 'store/contactTypes/actions';
import { selectContactTypesOptions } from 'store/contactTypes/selectors';
import {
  getApplicationBorrowersAction,
  getHousingFinanceAgenciesAction,
  getLoanAndLockSummaryEditDataAction,
  getLoanContactsAction,
  getPostClosingAction,
  getPropertyAndLoanSummaryEditDataAction,
} from 'store/loanApplications/actions';
import {
  selectChartLegend,
  selectCommonInfoLoanApplication,
  selectDealFromApplication,
  selectExtendedBorrowers,
  selectHiddenFieldIds,
  selectHousingFinanceAgenciesOptions,
  selectIsChattelLoanFromPropertyAndLoanSummary,
  selectIsHelocPurposePurchaseFromPropertyAndLoanSummary,
  selectIsLoanLenderEngineType,
  selectIsLoanNoPpeEngineType,
  selectIsLoanTBDBundleIssued,
  selectIsLoanPurposeAdditionalLoanFromPropertyAndLoanSummary,
  selectIsPurchaseLoanPurposeFromApplication,
  selectIsRefinanceLoanPurposeFromApplication,
  selectIsUnsecuredFromPropertyAndLoanSummary,
  selectLoanAndLockPendingChanges,
  selectLoanContacts,
  selectLoanNumber,
  selectLockedFieldIds,
  selectPostClosing,
  selectPropertyPendingChanges,
} from 'store/loanApplications/selectors';
import { loadLoanEditorCustomFormAction } from 'store/loanEditorCustomForms/actions';
import { getBuyDownsAction } from 'store/programs/actions';
import {
  getAppraisalProductsAction,
  getImpoundTypesAction,
  getInvestorFeatureCodesAction,
  getPostClosingOptionsAction,
  getProgramIdentifiersAction,
} from 'store/system/actions';
import { selectAppraisalProductsValues } from 'store/system/selectors';
import { selectCustomFields } from 'store/system/selectors/customFieldDefinitions.selectors';

import DrawerContent from './components/DrawerContent';
import useCloseAndReset from './hooks/useCloseAndReset';
import useClosingFundingContexts from './hooks/useClosingFundingContexts';
import useDrawerSave from './hooks/useDrawerSave';
import useFocusDrawerContent from './hooks/useFocusDrawerContent';
import useLoanAndLockContext from './hooks/useLoanAndLockContext';
import useLoanInfoFieldDefaults from './hooks/useLoanInfoFieldDefaults';
import { checkIsAppraisalEnabled } from './LoanEditorCustomFormDrawer.appraisal.utils';
import {
  buildDependentsCountChangeHandler,
  getBorrowerDependentsCount,
} from './LoanEditorCustomFormDrawer.borrower.utils';
import {
  buildEntityChangeState,
  summarizeSectionRequirements,
} from './LoanEditorCustomFormDrawer.changes.utils';
import {
  DRAWER_BUTTON_NAMES,
  EMPTY_LOAN_AND_LOCK_LIVE_VALUES,
  POST_CLOSING_FORM_PATH,
} from './LoanEditorCustomFormDrawer.constants';
import { buildEscrowMasterChangeHandler } from './LoanEditorCustomFormDrawer.escrow.utils';
import { reseedFormState } from './LoanEditorCustomFormDrawer.form.utils';
import { pickLoanAndLockLiveValues } from './LoanEditorCustomFormDrawer.loanAndLock.utils';
import { withDerivedDownPayment } from './LoanEditorCustomFormDrawer.loanInfoDefaults.utils';
import { buildPropertyPurposeContext } from './LoanEditorCustomFormDrawer.property.utils';
import {
  buildLoanAndLockMergedPendingChanges,
  buildPropertyMergedPendingChanges,
  checkIsSaveDisabled,
  logRejectedResults,
} from './LoanEditorCustomFormDrawer.save';
import { LoanEditorCustomFormDrawerTestIds } from './LoanEditorCustomFormDrawer.testIds';
import {
  buildBorrowerCanonicalConfig,
  buildClosingCanonicalConfig,
  buildContactCanonicalConfig,
  buildContactSelectorOptions,
  buildCustomFieldDefinitionsById,
  buildCustomFormAutoLockState,
  buildFundingCanonicalConfig,
  buildInapplicableEntries,
  buildLoanAndLockCanonicalConfig,
  buildPlacedCustomFieldDefinitionIds,
  buildPlacedFieldPaths,
  buildPostClosingCanonicalConfig,
  buildPropertyInfoCanonicalConfig,
  buildReadOnlyEntityKinds,
  buildSectionResolutions,
  buildUnsupportedEntries,
  checkHasAppraisalItems,
  checkHasPostClosingItems,
  checkHasRenderableSectionContent,
  checkHasSavableCustomFieldChanges,
  collectPlacedFieldPathsByEntity,
  collectPlacedPostClosingSectionKeys,
  DrawerEntityKind,
} from './LoanEditorCustomFormDrawer.utils';

const LoanEditorCustomFormDrawer = ({ open, onClose, customFormId, title = '' }) => {
  const dispatch = useDispatch();

  const { applicationId, loanId } = useSelector(selectDealFromApplication) ?? {};
  const loanNumber = useSelector(selectLoanNumber);
  const customFieldDefinitions = useSelector(selectCustomFields);
  const isPurchase = useSelector(selectIsPurchaseLoanPurposeFromApplication);
  const isRefinance = useSelector(selectIsRefinanceLoanPurposeFromApplication);
  const isAdditionalLoan = useSelector(selectIsLoanPurposeAdditionalLoanFromPropertyAndLoanSummary);
  const isHelocPurposePurchase = useSelector(
    selectIsHelocPurposePurchaseFromPropertyAndLoanSummary,
  );
  const remoteLoanAndLockPendingChanges = useSelector(selectLoanAndLockPendingChanges);
  const remotePropertyPendingChanges = useSelector(selectPropertyPendingChanges);
  const isLoanNoPpeEngineType = useSelector(selectIsLoanNoPpeEngineType);
  const housingFinanceAgenciesOptions = useSelector(selectHousingFinanceAgenciesOptions, _.isEqual);
  const { loanType, loanPurposeType } = useSelector(selectCommonInfoLoanApplication) ?? {};
  const { purchasePrice } = useSelector(selectChartLegend);
  const extendedBorrowers = useSelector(selectExtendedBorrowers);
  const loanContacts = useSelector(selectLoanContacts);
  const contactTypesOptions = useSelector(selectContactTypesOptions);
  const hiddenFieldIds = useSelector(selectHiddenFieldIds);
  const lockedFieldIds = useSelector(selectLockedFieldIds);
  const postClosing = useSelector(selectPostClosing);
  const investorsOptions = useSelector(selectInvestorsOptions);
  const servicingCompaniesOptions = useSelector(selectServicingCompaniesOptions);
  const postClosingSelectOptions = usePostClosingSelectOptions();
  const isUnsecured = useSelector(selectIsUnsecuredFromPropertyAndLoanSummary);
  const isLenderEngineType = useSelector(selectIsLoanLenderEngineType);
  const isBrokerOrSubCompanyUser = useSelector(selectIsBrokerOrSubCompanyUser);
  const isChattel = useSelector(selectIsChattelLoanFromPropertyAndLoanSummary);
  const isLoanTBDBundleIssued = useSelector(selectIsLoanTBDBundleIssued);
  const appraisalProducts = useSelector(selectAppraisalProductsValues);
  const { isViewAllowed: isPostClosingViewAllowed, isEditAllowed: isPostClosingEditAllowed } =
    useSelector(getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_POST_CLOSING));
  const { isViewAllowed: isAppraisalViewAllowed, isEditAllowed: isAppraisalEditAllowed } =
    useSelector(getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_APPRAISAL_SELECTION));
  const { isStrictEditAllowed: isLoanEditorEditAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_APPLICATION),
  );
  const { isEditAllowed: isContactsEditAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_CONTACTS),
  );
  const isMortgageInsuranceFieldsEnabled = useFeatureToggle(
    FeatureFlag.EnableMortgageInsuranceFields,
  );
  const isBplLoanPurposeType = checkIsLoanPurposeTypeBpl(loanPurposeType);

  const [form, setForm] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loanAndLockResponseData, setLoanAndLockResponseData] = useState(null);
  const [propertyResponseData, setPropertyResponseData] = useState(null);
  const [isPostClosingLoaded, setIsPostClosingLoaded] = useState(false);
  const [isAppraisalProductsLoaded, setIsAppraisalProductsLoaded] = useState(false);
  const [selectedBorrowerId, setSelectedBorrowerId] = useState(null);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [mortgageInsuranceLiveTypes, setMortgageInsuranceLiveTypes] = useState(null);
  const [loanAndLockLiveValues, setLoanAndLockLiveValues] = useState(
    EMPTY_LOAN_AND_LOCK_LIVE_VALUES,
  );
  const [liveBorrowerDependentsCount, setLiveBorrowerDependentsCount] = useState(null);

  const {
    closingContext,
    fundingContext,
    closingFundingApplicationData,
    closingSaveContext,
    storedFundingData,
    closingCascadeProps,
    closingCompanySelectorOptions,
    selectedClosingCompanyPrefix,
    setSelectedClosingCompanyPrefix,
    loadClosingFundingData,
    isClosingEditable,
    isFundingEditable,
    isClosingLoaded,
    isFundingLoaded,
  } = useClosingFundingContexts({ loanId, applicationId });

  const remoteLoanAndLockPendingChangesRef = useRef(remoteLoanAndLockPendingChanges);
  const remotePropertyPendingChangesRef = useRef(remotePropertyPendingChanges);
  const mortgageInsuranceCompanyTypeChangeRef = useRef(_.noop);
  const escrowMasterChangeRef = useRef(_.noop);
  const dependentsCountChangeRef = useRef(_.noop);
  const contentRef = useRef(null);
  const seededFormValuesRef = useRef({});

  const onMortgageInsuranceCompanyTypeChange = useCallback(
    (...args) => mortgageInsuranceCompanyTypeChangeRef.current(...args),
    [],
  );

  const onEscrowMasterChange = useCallback((...args) => escrowMasterChangeRef.current(...args), []);

  const onDependentsCountChange = useCallback(
    (...args) => dependentsCountChangeRef.current(...args),
    [],
  );

  const loanAndLockApplicationData = useMemo(() => {
    if (!loanAndLockResponseData) {
      return {};
    }

    return withDerivedDownPayment({
      applicationData: normalizeLoanAndLockApplicationData({
        data: loanAndLockResponseData,
        initialPendingChanges: remoteLoanAndLockPendingChangesRef.current ?? {},
        isPurchase,
        isAdditionalLoan,
        isHelocPurposePurchase,
        shouldShowLoanRolesSection: false,
        shouldShowLoanTagsSection: false,
        shouldShowProfitAndLossSection: false,
        shouldShowOBCustomFieldsSection: false,
      }),
      purchasePrice,
    });
  }, [
    isAdditionalLoan,
    isHelocPurposePurchase,
    isPurchase,
    loanAndLockResponseData,
    purchasePrice,
  ]);

  const propertyApplicationData = useMemo(() => {
    if (!propertyResponseData) {
      return {};
    }

    return normalizePropertyAndLoanApplicationData({
      data: propertyResponseData,
      initialPendingChanges: remotePropertyPendingChangesRef.current ?? {},
      isPurchase,
      isRefinance,
      isAdditionalLoan,
      isHelocPurposePurchase,
    });
  }, [isAdditionalLoan, isHelocPurposePurchase, isPurchase, isRefinance, propertyResponseData]);

  const borrowerApplicationData = useMemo(() => {
    if (!extendedBorrowers?.length) {
      return {};
    }

    return normalizeBorrowerApplicationData(extendedBorrowers);
  }, [extendedBorrowers]);

  const contactApplicationData = useMemo(() => {
    if (!loanContacts?.length) {
      return {};
    }

    return normalizeContactsApplicationData({ data: loanContacts });
  }, [loanContacts]);

  const postClosingApplicationData = useMemo(
    () =>
      isPostClosingLoaded
        ? { [POST_CLOSING_FORM_PATH]: buildPostClosingApplicationData(postClosing) }
        : {},
    [isPostClosingLoaded, postClosing],
  );

  const applicationData = useMemo(
    () => ({
      ...loanAndLockApplicationData,
      ...propertyApplicationData,
      ...borrowerApplicationData,
      ...contactApplicationData,
      ...postClosingApplicationData,
      ...closingFundingApplicationData,
    }),
    [
      borrowerApplicationData,
      closingFundingApplicationData,
      contactApplicationData,
      loanAndLockApplicationData,
      postClosingApplicationData,
      propertyApplicationData,
    ],
  );

  const sections = useMemo(() => form?.sections ?? [], [form]);

  const customFieldDefinitionsById = useMemo(
    () => buildCustomFieldDefinitionsById(customFieldDefinitions),
    [customFieldDefinitions],
  );

  const iframeTokenContext = useMemo(
    () => ({ loanId, loanNumber, borrowerId: selectedBorrowerId }),
    [loanId, loanNumber, selectedBorrowerId],
  );

  const isPostClosingEditable = isPostClosingEditAllowed && isPostClosingLoaded;

  const postClosingContext = useMemo(
    () => ({
      investorsOptions,
      servicingCompaniesOptions,
      isUnsecured,
      postClosingSelectOptions,
      isViewAllowed: isPostClosingViewAllowed,
      isEditAllowed: isPostClosingEditable,
    }),
    [
      investorsOptions,
      isPostClosingEditable,
      isPostClosingViewAllowed,
      isUnsecured,
      postClosingSelectOptions,
      servicingCompaniesOptions,
    ],
  );

  const borrowerDependentsContext = useMemo(
    () => ({
      dependentsCount:
        liveBorrowerDependentsCount ??
        getBorrowerDependentsCount({ applicationData, borrowerId: selectedBorrowerId }),
      onDependentsCountChange,
    }),
    [applicationData, liveBorrowerDependentsCount, onDependentsCountChange, selectedBorrowerId],
  );

  const mortgageInsuranceContext = useMemo(
    () => ({
      isEnabled:
        isMortgageInsuranceFieldsEnabled && isLenderEngineType && !!loanAndLockResponseData,
      miProductType: mortgageInsuranceLiveTypes?.miProductType ?? applicationData.miProductType,
      miCompanyType: mortgageInsuranceLiveTypes?.miCompanyType ?? applicationData.miCompanyType,
      onCompanyTypeChange: onMortgageInsuranceCompanyTypeChange,
      storedValues: loanAndLockApplicationData,
      isOrderOutcomeReadOnly: isBrokerOrSubCompanyUser,
    }),
    [
      applicationData.miCompanyType,
      applicationData.miProductType,
      isBrokerOrSubCompanyUser,
      isLenderEngineType,
      isMortgageInsuranceFieldsEnabled,
      loanAndLockApplicationData,
      loanAndLockResponseData,
      mortgageInsuranceLiveTypes,
      onMortgageInsuranceCompanyTypeChange,
    ],
  );

  const appraisalProductOptions = useMemo(
    () => buildAppraisalProductOptions(appraisalProducts),
    [appraisalProducts],
  );

  const appraisalContext = useMemo(
    () => ({
      isEnabled: checkIsAppraisalEnabled({
        isProductsLoaded: isAppraisalProductsLoaded,
        isSelectionViewAllowed: isAppraisalViewAllowed,
        hasPropertyData: !!propertyResponseData,
        isUnsecured,
        isChattel,
      }),
      isViewAllowed: isAppraisalViewAllowed,
      isEditAllowed: isAppraisalEditAllowed,
      numberOfUnits: propertyApplicationData.numberOfUnits,
      loanType,
      isLoanTBDBundleIssued,
      appraisalProductOptions,
    }),
    [
      appraisalProductOptions,
      isAppraisalEditAllowed,
      isAppraisalProductsLoaded,
      isAppraisalViewAllowed,
      isChattel,
      isLoanTBDBundleIssued,
      isUnsecured,
      loanType,
      propertyApplicationData.numberOfUnits,
      propertyResponseData,
    ],
  );

  const propertyPurposeContext = useMemo(
    () =>
      buildPropertyPurposeContext({
        isPurchase,
        isRefinance,
        isAdditionalLoan,
        isHelocPurposePurchase,
        transactionDetailType: propertyApplicationData.transactionDetail,
      }),
    [
      isAdditionalLoan,
      isHelocPurposePurchase,
      isPurchase,
      isRefinance,
      propertyApplicationData.transactionDetail,
    ],
  );

  const loanAndLockContext = useLoanAndLockContext({
    applicationData,
    liveValues: loanAndLockLiveValues,
    loanAndLockResponseData,
    housingFinanceAgenciesOptions,
    isLoanNoPpeEngineType,
    onEscrowMasterChange,
  });

  const readOnlyEntityKinds = useMemo(
    () =>
      buildReadOnlyEntityKinds({
        isLoanEditorEditAllowed,
        isContactsEditAllowed,
      }),
    [isContactsEditAllowed, isLoanEditorEditAllowed],
  );

  const autoLockState = useMemo(
    () =>
      buildCustomFormAutoLockState({
        slug: form?.slug ?? null,
        hiddenFieldIds,
        lockedFieldIds,
      }),
    [form?.slug, hiddenFieldIds, lockedFieldIds],
  );

  const sectionResolutions = useMemo(
    () =>
      sections.map(section => ({
        section,
        ...buildSectionResolutions({
          kind: section.kind,
          iframeUrl: section.iframeUrl,
          iframeHeightPx: section.iframeHeightPx,
          items: section.items,
          customFieldDefinitionsById,
          borrowerEntityId: selectedBorrowerId,
          borrowerDependentsContext,
          selectedContactId,
          contactTypesOptions,
          postClosingContext,
          mortgageInsuranceContext,
          appraisalContext,
          closingContext,
          fundingContext,
          loanAndLockContext,
          propertyPurposeContext,
          readOnlyEntityKinds,
          autoLockState,
        }),
      })),
    [
      appraisalContext,
      autoLockState,
      borrowerDependentsContext,
      closingContext,
      contactTypesOptions,
      customFieldDefinitionsById,
      fundingContext,
      loanAndLockContext,
      mortgageInsuranceContext,
      postClosingContext,
      propertyPurposeContext,
      readOnlyEntityKinds,
      sections,
      selectedBorrowerId,
      selectedContactId,
    ],
  );

  const renderableSectionResolutions = useMemo(
    () => sectionResolutions.filter(checkHasRenderableSectionContent),
    [sectionResolutions],
  );

  const unsupportedEntries = useMemo(
    () => buildUnsupportedEntries(sectionResolutions),
    [sectionResolutions],
  );

  const inapplicableEntries = useMemo(
    () => buildInapplicableEntries(sectionResolutions),
    [sectionResolutions],
  );

  const customFieldsController = useCustomFieldsValidation({
    applicationId,
    target: CUSTOM_FIELDS_TARGET.LOAN,
    targetEntityId: applicationId,
    definitionIds: buildPlacedCustomFieldDefinitionIds(sectionResolutions),
    skip: _.isNil(applicationId) || !open,
  });
  const {
    dirtyValues: customFieldsDirtyValues,
    isValid: areCustomFieldsValid,
    markAllTouched: markAllCustomFieldsTouched,
    save: saveCustomFields,
    discard: discardCustomFields,
  } = customFieldsController;

  const allLoanFieldGroups = useMemo(
    () => sectionResolutions.flatMap(({ loanFieldGroups: groups }) => groups),
    [sectionResolutions],
  );

  const fieldPathsByEntity = useMemo(
    () => collectPlacedFieldPathsByEntity(sectionResolutions),
    [sectionResolutions],
  );

  const postClosingFieldPaths = fieldPathsByEntity[DrawerEntityKind.POST_CLOSING];

  const postClosingSectionKeys = useMemo(
    () => collectPlacedPostClosingSectionKeys(postClosingFieldPaths),
    [postClosingFieldPaths],
  );

  const hasPostClosingData = isPostClosingLoaded && !!postClosingSectionKeys.size;

  const mergedCanonicalConfig = useMemo(() => {
    const hasLoanAndLockData = !_.isNil(loanAndLockResponseData);
    const hasPropertyData = !_.isNil(propertyResponseData);
    const borrowerCanonicalConfig = buildBorrowerCanonicalConfig({
      loanFieldGroups: allLoanFieldGroups,
      borrowerEntityId: selectedBorrowerId,
      borrowerDependentsContext,
    });
    const contactCanonicalConfig = buildContactCanonicalConfig({
      loanFieldGroups: allLoanFieldGroups,
      selectedContactId,
      contactTypesOptions,
    });
    const postClosingCanonicalConfig = hasPostClosingData
      ? buildPostClosingCanonicalConfig({ postClosingFieldPaths, postClosingContext })
      : { groups: [] };
    const closingFundingGroups = [
      ...buildClosingCanonicalConfig({ loanFieldGroups: allLoanFieldGroups, closingContext })
        .groups,
      ...buildFundingCanonicalConfig({ loanFieldGroups: allLoanFieldGroups, fundingContext })
        .groups,
    ];
    const hasBorrowerGroups = !!borrowerCanonicalConfig.groups.length;
    const hasContactGroups = !!contactCanonicalConfig.groups.length;

    if (
      !hasLoanAndLockData &&
      !hasPropertyData &&
      !hasBorrowerGroups &&
      !hasContactGroups &&
      !hasPostClosingData &&
      !closingFundingGroups.length
    ) {
      return { groups: [] };
    }

    const loanAndLockConfig = hasLoanAndLockData
      ? buildLoanAndLockCanonicalConfig({
          loanAndLockContext,
          loanFieldGroups: allLoanFieldGroups,
          mortgageInsuranceContext,
        })
      : { groups: [] };

    const propertyInfoConfig = hasPropertyData
      ? buildPropertyInfoCanonicalConfig({
          loanFieldGroups: allLoanFieldGroups,
          propertyApplicationData,
          appraisalContext,
        })
      : { groups: [] };

    return {
      groups: [
        ...loanAndLockConfig.groups,
        ...propertyInfoConfig.groups,
        ...borrowerCanonicalConfig.groups,
        ...contactCanonicalConfig.groups,
        ...postClosingCanonicalConfig.groups,
        ...closingFundingGroups,
      ],
    };
  }, [
    allLoanFieldGroups,
    appraisalContext,
    borrowerDependentsContext,
    closingContext,
    contactTypesOptions,
    fundingContext,
    hasPostClosingData,
    loanAndLockContext,
    loanAndLockResponseData,
    mortgageInsuranceContext,
    postClosingContext,
    postClosingFieldPaths,
    propertyApplicationData,
    propertyResponseData,
    selectedBorrowerId,
    selectedContactId,
  ]);

  const { initialFormValues, schema, fieldsConfig, observableFieldsNames } = useMemo(
    () => getConfigInfo({ applicationData, config: mergedCanonicalConfig, isNestedForm: true }),
    [applicationData, mergedCanonicalConfig],
  );

  const {
    form: formState,
    formRef,
    handleChange,
    validationErrors,
    setSchema,
    setInForm,
    setMultipleInForm,
    setForm: setFormState,
    setInitialFormValues,
    setIsTouched,
    setValidationErrors,
    isFormChanged,
    validateField,
    validateForm,
    staticValidateField,
    setExcludedValidationPaths,
    formPendingChanges,
  } = useForm(initialFormValues, fieldsConfig, observableFieldsNames);

  const excludedValidationPaths = useMemo(() => {
    const placedFieldPaths = buildPlacedFieldPaths(sectionResolutions);

    return observableFieldsNames.filter(fieldName => !placedFieldPaths.has(fieldName));
  }, [observableFieldsNames, sectionResolutions]);

  const liveMiProductType = formState?.miProductType?.value ?? null;
  const liveMiCompanyType = formState?.miCompanyType?.value ?? null;
  const hasMiCompanyOtherDescription = !_.isNil(formState?.miCompanyOtherDescription);
  const liveLoanAndLockValues = useMemo(
    () => pickLoanAndLockLiveValues({ formState, formPendingChanges }),
    [formPendingChanges, formState],
  );

  const loanAndLockMergedPendingChanges = useMemo(
    () =>
      buildLoanAndLockMergedPendingChanges({
        loanAndLockResponseData,
        remoteLoanAndLockPendingChanges,
        formPendingChanges,
        applicationData,
        isPurchase,
        isAdditionalLoan,
        isHelocPurposePurchase,
        housingFinanceAgenciesOptions,
        isLoanNoPpeEngineType,
      }),
    [
      applicationData,
      formPendingChanges,
      housingFinanceAgenciesOptions,
      isAdditionalLoan,
      isHelocPurposePurchase,
      isLoanNoPpeEngineType,
      isPurchase,
      remoteLoanAndLockPendingChanges,
      loanAndLockResponseData,
    ],
  );

  const propertyMergedPendingChanges = useMemo(
    () =>
      buildPropertyMergedPendingChanges({
        propertyResponseData,
        remotePropertyPendingChanges,
        formPendingChanges,
        applicationData,
        isPurchase,
        isAdditionalLoan,
        isHelocPurposePurchase,
        housingFinanceAgenciesOptions,
        loanType,
        isLoanNoPpeEngineType,
      }),
    [
      applicationData,
      formPendingChanges,
      housingFinanceAgenciesOptions,
      isAdditionalLoan,
      isHelocPurposePurchase,
      isLoanNoPpeEngineType,
      isPurchase,
      loanType,
      propertyResponseData,
      remotePropertyPendingChanges,
    ],
  );

  const mergedPendingChanges = useMemo(
    () => ({ ...loanAndLockMergedPendingChanges, ...propertyMergedPendingChanges }),
    [loanAndLockMergedPendingChanges, propertyMergedPendingChanges],
  );

  const {
    loanAndLockEditedFieldPaths,
    hasLoanAndLockChanges,
    hasPropertyInfoChanges,
    hasBorrowerChanges,
    hasContactChanges,
    hasPostClosingChanges,
    hasClosingChanges,
    hasFundingChanges,
  } = useMemo(
    () =>
      buildEntityChangeState({
        formPendingChanges,
        fieldPathsByEntity,
        isPostClosingEditable,
        isClosingEditable,
        isFundingEditable,
      }),
    [
      fieldPathsByEntity,
      formPendingChanges,
      isClosingEditable,
      isFundingEditable,
      isPostClosingEditable,
    ],
  );

  const {
    requiresBorrowerSelection,
    requiresContactSelection,
    requiresClosingCompanySelection,
    hasBorrowerFields,
    hasContactFields,
    hasClosingCompanyFields,
  } = useMemo(() => summarizeSectionRequirements(sectionResolutions), [sectionResolutions]);

  const borrowerSelectorOptions = useMemo(
    () =>
      (extendedBorrowers ?? []).map(borrower => ({
        id: borrower.id,
        fullName: borrower.fullName,
      })),
    [extendedBorrowers],
  );

  const contactSelectorOptions = useMemo(
    () => buildContactSelectorOptions(loanContacts),
    [loanContacts],
  );

  const hasLoanFieldChanges =
    !!isFormChanged &&
    (hasLoanAndLockChanges ||
      hasPropertyInfoChanges ||
      hasBorrowerChanges ||
      hasContactChanges ||
      hasPostClosingChanges ||
      hasClosingChanges ||
      hasFundingChanges);
  const hasCustomFieldChanges = checkHasSavableCustomFieldChanges({
    dirtyValues: customFieldsDirtyValues,
    sectionResolutions,
  });
  const hasAnyChanges = hasLoanFieldChanges || hasCustomFieldChanges;

  const hasAnyEntityData =
    !!loanAndLockResponseData ||
    !!propertyResponseData ||
    hasBorrowerFields ||
    hasContactFields ||
    hasPostClosingData ||
    isClosingLoaded ||
    isFundingLoaded;

  const entitySaveContext = useMemo(
    () => ({
      applicationId,
      loanId,
      formState,
      applicationData,
      loanType,
      isLoanNoPpeEngineType,
      isBplLoanPurposeType,
      loanAndLockResponseData,
      selectedBorrowerId,
      selectedContactId,
      loanAndLockEditedFieldPaths,
      loanAndLockHiddenFieldPaths: loanAndLockContext.hiddenFieldPaths,
      hasPropertyInfoChanges,
      hasBorrowerChanges,
      hasContactChanges,
      postClosing,
      postClosingSectionKeys,
      hasPostClosingChanges,
      formPendingChanges,
      closingSaveContext,
      hasClosingChanges,
      storedFundingData,
      hasFundingChanges,
    }),
    [
      applicationData,
      applicationId,
      closingSaveContext,
      formPendingChanges,
      formState,
      hasBorrowerChanges,
      hasClosingChanges,
      hasContactChanges,
      hasFundingChanges,
      hasPostClosingChanges,
      hasPropertyInfoChanges,
      isBplLoanPurposeType,
      isLoanNoPpeEngineType,
      loanAndLockContext.hiddenFieldPaths,
      loanAndLockEditedFieldPaths,
      loanAndLockResponseData,
      loanId,
      loanType,
      postClosing,
      postClosingSectionKeys,
      selectedBorrowerId,
      selectedContactId,
      storedFundingData,
    ],
  );

  const resetSession = useCallback(() => {
    setForm(null);
    setLoanAndLockResponseData(null);
    setPropertyResponseData(null);
    setIsPostClosingLoaded(false);
    setIsAppraisalProductsLoaded(false);
    setIsLoading(false);
    setIsSaving(false);
    setSelectedBorrowerId(null);
    setSelectedContactId(null);
    setMortgageInsuranceLiveTypes(null);
    setLoanAndLockLiveValues(EMPTY_LOAN_AND_LOCK_LIVE_VALUES);
    setLiveBorrowerDependentsCount(null);
    seededFormValuesRef.current = {};
  }, []);

  const onSelectBorrower = useCallback(
    borrowerId => {
      setSelectedBorrowerId(borrowerId);
      setLiveBorrowerDependentsCount(
        getBorrowerDependentsCount({ formState: formRef.current, applicationData, borrowerId }),
      );
    },
    [applicationData, formRef],
  );

  const onDrawerClose = useCloseAndReset({
    onClose,
    resetSession,
    setFormState,
    setInitialFormValues,
    setIsTouched,
    setValidationErrors,
    discardCustomFields,
  });

  const onSave = useDrawerSave({
    contentRef,
    hasAnyEntityData,
    requiresBorrowerSelection:
      requiresBorrowerSelection || (hasBorrowerFields && _.isNil(selectedBorrowerId)),
    requiresContactSelection:
      requiresContactSelection || (hasContactFields && _.isNil(selectedContactId)),
    requiresClosingCompanySelection,
    validateForm,
    setValidationErrors,
    areCustomFieldsValid,
    markAllCustomFieldsTouched,
    hasCustomFieldChanges,
    saveCustomFields,
    entitySaveContext,
    setIsSaving,
    onClose: onDrawerClose,
  });

  const isSaveDisabled = checkIsSaveDisabled({
    isLoading,
    isSaving,
    hasAnyChanges,
    hasAnyEntityData,
    requiresBorrowerSelection,
    requiresContactSelection,
    requiresClosingCompanySelection,
  });

  const drawerContent = (
    <DrawerContent
      borrowerSelectorOptions={borrowerSelectorOptions}
      closingCascadeProps={closingCascadeProps}
      closingCompanySelectorOptions={closingCompanySelectorOptions}
      contactSelectorOptions={contactSelectorOptions}
      contentRef={contentRef}
      customFieldsController={customFieldsController}
      form={form}
      formState={formState}
      handleChange={handleChange}
      hasBorrowerFields={hasBorrowerFields}
      hasClosingCompanyFields={hasClosingCompanyFields}
      hasContactFields={hasContactFields}
      iframeTokenContext={iframeTokenContext}
      inapplicableEntries={inapplicableEntries}
      isLoading={isLoading}
      isSaving={isSaving}
      mergedPendingChanges={mergedPendingChanges}
      sectionResolutions={renderableSectionResolutions}
      selectedBorrowerId={selectedBorrowerId}
      selectedClosingCompanyPrefix={selectedClosingCompanyPrefix}
      selectedContactId={selectedContactId}
      setInForm={setInForm}
      staticValidateField={staticValidateField}
      unsupportedEntries={unsupportedEntries}
      validateField={validateField}
      validationErrors={validationErrors}
      onSelectBorrower={onSelectBorrower}
      onSelectClosingCompany={setSelectedClosingCompanyPrefix}
      onSelectContact={setSelectedContactId}
    />
  );

  const drawerActions = useMemo(
    () => (
      <Button
        data-testid={LoanEditorCustomFormDrawerTestIds.SAVE_BUTTON}
        disabled={isSaveDisabled}
        variant="contained"
        onClick={onSave}
      >
        {DRAWER_BUTTON_NAMES.ACCEPT}
      </Button>
    ),
    [isSaveDisabled, onSave],
  );

  useEffect(() => {
    remoteLoanAndLockPendingChangesRef.current = remoteLoanAndLockPendingChanges;
  }, [remoteLoanAndLockPendingChanges]);

  useEffect(() => {
    remotePropertyPendingChangesRef.current = remotePropertyPendingChanges;
  }, [remotePropertyPendingChanges]);

  useEffectSafeStrictMode(() => {
    if (!open || _.isNil(customFormId)) {
      return;
    }

    setIsLoading(true);

    const formRequest = dispatch(
      loadLoanEditorCustomFormAction({ id: customFormId, includeGlobal: true }),
    );
    const hasApplication = !_.isNil(applicationId);
    const loanRequest = hasApplication
      ? dispatch(getLoanAndLockSummaryEditDataAction({ applicationId }))
      : Promise.resolve(null);
    const propertyRequest = hasApplication
      ? dispatch(getPropertyAndLoanSummaryEditDataAction({ applicationId }))
      : Promise.resolve(null);

    Promise.all([formRequest, loanRequest, propertyRequest])
      .then(async ([formDetails, loanAndLockResult, propertyResult]) => {
        const shouldLoadPostClosing =
          isPostClosingViewAllowed &&
          checkHasPostClosingItems(formDetails?.sections) &&
          !_.isNil(loanId);
        const shouldLoadAppraisal =
          isAppraisalViewAllowed && checkHasAppraisalItems(formDetails?.sections);

        const [postClosingResults, appraisalResults] = await Promise.all([
          shouldLoadPostClosing
            ? Promise.allSettled([
                dispatch(getPostClosingAction({ loanId })),
                dispatch(getInvestorsAction()),
                dispatch(getServicingCompaniesAction()),
                dispatch(getPostClosingOptionsAction()),
              ])
            : null,
          shouldLoadAppraisal ? Promise.allSettled([dispatch(getAppraisalProductsAction())]) : null,
          loadClosingFundingData(formDetails?.sections),
        ]);

        if (postClosingResults) {
          setIsPostClosingLoaded(!logRejectedResults(postClosingResults).length);
        }

        if (appraisalResults) {
          setIsAppraisalProductsLoaded(!logRejectedResults(appraisalResults).length);
        }

        setForm(formDetails ?? null);

        if (loanAndLockResult?.payload) {
          setLoanAndLockResponseData(loanAndLockResult.payload);
        }

        if (propertyResult?.payload) {
          setPropertyResponseData(propertyResult.payload);
        }
      })
      .catch(error => {
        logger.error(error);
      })
      .finally(() => setIsLoading(false));
  }, [
    applicationId,
    customFormId,
    dispatch,
    isAppraisalViewAllowed,
    isPostClosingViewAllowed,
    loadClosingFundingData,
    loanId,
    open,
  ]);

  useEffectSafeStrictMode(() => {
    if (!open || _.isNil(applicationId)) {
      return;
    }

    Promise.all([
      dispatch(getProgramIdentifiersAction()),
      dispatch(getInvestorFeatureCodesAction()),
      dispatch(getBuyDownsAction()),
      dispatch(getLeadSourcesAction({ applicationId })),
      dispatch(getImpoundTypesAction({ includeDeleted: true })),
      dispatch(getApplicationBorrowersAction({ applicationId })),
      dispatch(getLoanContactsAction({ applicationId })),
      dispatch(getContactTypesAction()),
      dispatch(getHousingFinanceAgenciesAction()),
    ]).catch(error => {
      logger.error(error);
    });
  }, [applicationId, dispatch, open]);

  useFocusDrawerContent({ isReady: open && !isLoading && !!form, contentRef });

  useLoanInfoFieldDefaults({
    applicationData,
    formState,
    initialFormValues,
    placedFieldPaths: fieldPathsByEntity[DrawerEntityKind.LOAN_AND_LOCK],
    purchasePrice,
    setInForm,
    setMultipleInForm,
  });

  useEffect(() => {
    setSchema(schema);
  }, [setSchema, schema]);

  useEffect(() => {
    setExcludedValidationPaths(excludedValidationPaths);
  }, [excludedValidationPaths, setExcludedValidationPaths]);

  useEffect(() => {
    const previousInitialValues = seededFormValuesRef.current;

    seededFormValuesRef.current = initialFormValues;
    setInitialFormValues(initialFormValues);
    setFormState(currentForm =>
      reseedFormState({
        currentForm,
        previousInitialValues,
        nextInitialValues: initialFormValues,
      }),
    );
  }, [initialFormValues, setFormState, setInitialFormValues]);

  useEffect(() => {
    setMortgageInsuranceLiveTypes({
      miProductType: liveMiProductType,
      miCompanyType: liveMiCompanyType,
    });
  }, [liveMiCompanyType, liveMiProductType]);

  useEffect(() => {
    setLoanAndLockLiveValues(currentValues =>
      _.isEqual(currentValues, liveLoanAndLockValues) ? currentValues : liveLoanAndLockValues,
    );
  }, [liveLoanAndLockValues]);

  useEffect(() => {
    mortgageInsuranceCompanyTypeChangeRef.current = buildMiCompanyTypeChangeHandler({
      hasCompanyOtherDescription: hasMiCompanyOtherDescription,
      setMultipleInForm,
      validateField,
    });
  }, [hasMiCompanyOtherDescription, setMultipleInForm, validateField]);

  useEffect(() => {
    dependentsCountChangeRef.current = buildDependentsCountChangeHandler({
      entityId: selectedBorrowerId,
      formRef,
      setMultipleInForm,
      validateField,
      onCountChange: setLiveBorrowerDependentsCount,
    });
  }, [formRef, selectedBorrowerId, setMultipleInForm, validateField]);

  useEffect(() => {
    escrowMasterChangeRef.current = buildEscrowMasterChangeHandler({
      insuranceIds: loanAndLockContext.escrowConfigInputs.insuranceIds,
      floodInsuranceImpoundId: loanAndLockContext.escrowConfigInputs.floodInsuranceImpoundId,
      handleChange,
      setMultipleInForm,
    });
  }, [handleChange, loanAndLockContext.escrowConfigInputs, setMultipleInForm]);

  return (
    <DrawerCDS
      actions={drawerActions}
      content={drawerContent}
      isDirty={hasAnyChanges}
      isLoading={isSaving}
      open={open}
      title={title}
      onClose={onDrawerClose}
    />
  );
};

LoanEditorCustomFormDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customFormId: PropTypes.string,
  title: PropTypes.string,
};

LoanEditorCustomFormDrawer.displayName = 'LoanEditorCustomFormDrawer';

export default memo(LoanEditorCustomFormDrawer);
